import React, { useState, useEffect } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { Lock, Eye, EyeOff } from 'lucide-react';
import { Header } from '../components/Header';
import { Button } from '../components/ui/button';

const PartnerSetPasswordPage = () => {
  const navigate = useNavigate();
  const { token: tokenFromParams } = useParams();
  const [searchParams] = useSearchParams();
  const tokenFromQuery = searchParams.get('token');
  const [token, setToken] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [verifying, setVerifying] = useState(true);
  const [error, setError] = useState('');
  const [partnerInfo, setPartnerInfo] = useState(null);
  const [resending, setResending] = useState(false);
  const [resendSuccess, setResendSuccess] = useState(false);

  useEffect(() => {
    // Support both URL params (/partner/set-password/:token) and query params (?token=xxx)
    const t = tokenFromParams || tokenFromQuery;
    console.log('🔍 Token from params:', tokenFromParams, 'from query:', tokenFromQuery);
    
    if (!t) {
      setError('Kein Token gefunden');
      setVerifying(false);
      return;
    }
    setToken(t);
    verifyToken(t);
  }, [tokenFromParams, tokenFromQuery]);

  const verifyToken = async (t) => {
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/auth/verify-partner-token/${t}`);
      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.detail || 'Ungültiger oder abgelaufener Link');
      }

      setPartnerInfo(data);
    } catch (err) {
      setError(err.message);
    } finally {
      setVerifying(false);
    }
  };

  const handleResendLink = async () => {
    if (!partnerInfo?.email) return;
    
    setResending(true);
    setResendSuccess(false);
    
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/auth/resend-verification`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: partnerInfo.email })
      });

      if (response.ok) {
        setResendSuccess(true);
      }
    } catch (err) {
      // Silent fail
    } finally {
      setResending(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (password.length < 8) {
      setError('Passwort muss mindestens 8 Zeichen lang sein');
      return;
    }

    if (password !== confirmPassword) {
      setError('Passwörter stimmen nicht überein');
      return;
    }

    setLoading(true);

    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/auth/partner/set-password`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ token, password })
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.detail || 'Fehler beim Setzen des Passworts');
      }

      // Auto-Login: Store JWT token
      localStorage.setItem('partner_token', data.token);
      localStorage.setItem('partner_id', data.partner.id);
      localStorage.setItem('partner_email', data.partner.email);

      // Redirect to dashboard
      navigate('/portal/dashboard');
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  if (verifying) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <p className="text-gray-600">Verifiziere Link...</p>
      </div>
    );
  }

  if (error && !partnerInfo) {
    return (
      <div className="min-h-screen bg-gray-50">
        <Header />
        <main className="pt-32 pb-16">
          <div className="max-w-md mx-auto px-6">
            <div className="bg-white border-2 border-red-200 rounded-3xl p-10 text-center">
              <h1 className="text-2xl font-bold text-red-700 mb-4">Ungültiger Link</h1>
              <p className="text-gray-600 mb-6">{error}</p>
              
              {resendSuccess && (
                <div className="bg-green-50 border border-green-200 rounded-xl p-4 mb-6">
                  <p className="text-sm text-green-700">
                    Ein neuer Zugangslink wurde versendet. Bitte prüfen Sie Ihr E-Mail-Postfach.
                  </p>
                </div>
              )}
              
              {error.includes('abgelaufen') && (
                <button
                  onClick={handleResendLink}
                  disabled={resending}
                  className="w-full mb-4 px-6 py-3 bg-brand-green hover:bg-brand-green-dark text-white rounded-xl font-semibold disabled:opacity-50"
                >
                  {resending ? 'Wird gesendet...' : 'Link erneut senden'}
                </button>
              )}
              
              <a href="/portal/login" className="text-brand-green hover:text-brand-green-dark font-semibold underline">
                Zum Login
              </a>
            </div>
          </div>
        </main>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <Header />
      
      <main className="pt-32 pb-16">
        <div className="max-w-md mx-auto px-6">
          <div className="bg-white border-2 border-gray-200 rounded-3xl p-10 shadow-lg">
            <div className="text-center mb-8">
              <div className="w-16 h-16 bg-brand-green/10 rounded-2xl flex items-center justify-center mx-auto mb-4">
                <Lock className="w-8 h-8 text-brand-green" />
              </div>
              <h1 className="text-3xl font-bold text-gray-900 mb-2">
                Passwort festlegen
              </h1>
              <p className="text-gray-600">
                Willkommen, {partnerInfo?.email}
              </p>
            </div>

            <form onSubmit={handleSubmit} className="space-y-6">
              <div>
                <label htmlFor="password" className="block text-sm font-semibold text-gray-900 mb-2">
                  Passwort *
                </label>
                <div className="relative">
                  <input
                    type={showPassword ? 'text' : 'password'}
                    id="password"
                    required
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-brand-green"
                    placeholder="Mindestens 8 Zeichen"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700"
                  >
                    {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                  </button>
                </div>
              </div>

              <div>
                <label htmlFor="confirmPassword" className="block text-sm font-semibold text-gray-900 mb-2">
                  Passwort bestätigen *
                </label>
                <input
                  type={showPassword ? 'text' : 'password'}
                  id="confirmPassword"
                  required
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-brand-green"
                  placeholder="Passwort wiederholen"
                />
              </div>

              {error && (
                <div className="bg-red-50 border border-red-200 rounded-xl p-4">
                  <p className="text-sm text-red-700">{error}</p>
                </div>
              )}

              <Button
                type="submit"
                disabled={loading}
                className="w-full bg-brand-green hover:bg-brand-green-dark text-white py-3 rounded-xl font-semibold"
              >
                {loading ? 'Wird gespeichert...' : 'Passwort festlegen & Einloggen'}
              </Button>
            </form>
          </div>
        </div>
      </main>
    </div>
  );
};

export default PartnerSetPasswordPage;
